import React, { useEffect, useState } from 'react'
import { EditorState, convertToRaw, convertFromHTML, ContentState, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import base64 from 'base-64';
import utf8 from 'utf8';
import { Button, Typography, makeStyles } from '@material-ui/core';
import { Editor } from 'react-draft-wysiwyg';
import "../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css"
import styles from "./styles";
import { useSelector } from 'react-redux';
import { getThemes } from '../../reducers/portalConfig';
import { getI18n } from '../../utils/intl';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { DEFAULT_TYPOGRAPHY_OPTIONS, getCookieMessage } from '../../pages/Appearance/Edit/utils';

function CookieConsent(props) {
  const {
    value,
    onChange,
    helpertext,
    defaultCookieMessage,
    cookieMessageFromApi,
    editorContainerClass,
  } = props


  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [restoreDefault, setRestoreDefault] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false);

  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const intl = getI18n(useIntl());

  const fontFamily = DEFAULT_TYPOGRAPHY_OPTIONS.map((font) => font.name);

  const fontSizes = useSelector((state) => getThemes(state));
  const fontSize = fontSizes.fontSize;

  let uniqueFontSizes = [];
  if (fontSize) {
    Object.keys(fontSize)?.forEach(key => {
      if (uniqueFontSizes.indexOf(fontSize[key]?.replace('px', '')) === -1) {
        uniqueFontSizes.push(fontSize[key]?.replace('px', ''));
      }
    });
    uniqueFontSizes = uniqueFontSizes.sort();
  }

  useEffect(() => {
    const cookieMessageFromApiConverted = getCookieMessage(cookieMessageFromApi);

    if (!isFormDirty && cookieMessageFromApi !== value && !restoreDefault) {
      const contentBlock = htmlToDraft(cookieMessageFromApiConverted);
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      setEditorState(EditorState.createWithContent(contentState));
      onChange(base64.encode(utf8.encode(cookieMessageFromApiConverted)))
    }
  }, [cookieMessageFromApi, isFormDirty, value])

  useEffect(() => {
    const msg = value ? getCookieMessage(value) : defaultCookieMessage;
    const contentBlock = htmlToDraft(msg);
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
    const newEditorState = EditorState.createWithContent(contentState);
    onChange(base64.encode(utf8.encode(msg)));
    setEditorState(newEditorState);
  }, []);

  useEffect(() => {
    if (restoreDefault !== undefined) {
      onChange(base64.encode(utf8.encode(defaultCookieMessage)));
      setEditorState(
        EditorState.createWithContent(ContentState.createFromBlockArray(
          convertFromHTML(defaultCookieMessage))));
    }
  }, [restoreDefault]);

  const onEditorStateChange = (editorState) => {
    let value = '';
    setIsFormDirty(true)
    if (convertToRaw(editorState.getCurrentContent()) &&
      convertToRaw(editorState.getCurrentContent()).blocks.length > 0 &&
      convertToRaw(editorState.getCurrentContent()).blocks[0].text.trim()) {
      value = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    } else {
      value = defaultCookieMessage;
    }
    onChange(base64.encode(utf8.encode(value)));
    setEditorState(editorState);
  };

  const handlePastedText = (text) => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    const allowedText = text.substring(0, 401 - currentContentLength);
    if (currentContentLength + text.length > 400) {
      const modifiedText = Modifier.replaceText(editorState.getCurrentContent(),
        editorState.getSelection(), allowedText);
      const newEditorState = EditorState.createWithContent(modifiedText);
      setEditorState(newEditorState);
      return 'handled';
    }
    return '';
  };

  const handleBeforeInput = () => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    if (currentContentLength > 400) {
      return 'handled';
    }
    return '';
  };

  const editorContainer = clsx(classes.editorContainer, editorContainerClass);

  return (
    <div className={editorContainer}>
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        toolbarClassName={classes.editorWrapper}
        handleBeforeInput={handleBeforeInput}
        handlePastedText={handlePastedText}
        editorClassName={classes.editor}
        toolbar={{
          options: ['fontFamily', 'fontSize', 'inline', 'link'],
          inline: {
            options: ['bold'],
            bold: { className: classes.editorFontType },
          },
          link: {
            inDropdown: true,
            className: classes.editorLink,
            showOpenOptionOnHover: true,
            defaultTargetOption: '_blank',
          },
          fontFamily: {
            options: fontFamily,
            className: classes.editorFontFamily,
          },
          fontSize: {
            options: uniqueFontSizes,
            className: classes.editorFontSize,
          },
        }}
      />
      <Typography className={classes.helperText}>{helpertext}</Typography>
      <Button
        className={classes.restoreDefaultButton}
        variant='text'
        color='secondary'
        onClick={() => setRestoreDefault(value => !value)}
      >
        {intl.getI18nMessage('label.appearance.form.editor.restore.default.button.text')}
      </Button>
    </div>
  )
}

CookieConsent.propTypes = {
  restoreDefaultButton: PropTypes.bool,
  helpertext: PropTypes.string,
  defaultCookieMessage: PropTypes.string,
  cookieMessageFromApi: PropTypes.string,
  editorContainerClass: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
}

export default CookieConsent;
